import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import useMovieTrailer from "../hooks/useMovieTrailer";

const Modal = ({ movieId, onClose }) => {
	const { trailerVideo, loading, error } = useMovieTrailer(movieId);
	const [iframeLoading, setIframeLoading] = useState(true);

	// Handle ESC key press
	useEffect(() => {
		const handleEscKey = (event) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("keydown", handleEscKey);
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", handleEscKey);
			document.body.style.overflow = "unset";
		};
	}, [onClose]);

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const handleIframeLoad = () => {
		setIframeLoading(false);
	};

	// Show loading state
	if (loading) {
		return createPortal(
			<div
				className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center"
				style={{ zIndex: 999999 }} // Use inline style for maximum specificity
				onClick={handleBackdropClick}
			>
				<button
					onClick={onClose}
					className="absolute top-8 right-8 text-white hover:text-red-500 transition-colors bg-black bg-opacity-70 rounded-full p-3 shadow-lg"
					style={{ zIndex: 1000000 }}
					aria-label="Close modal"
				>
					<IoClose size={32} />
				</button>
			</div>,
			document.body
		);
	}

	// Show error state
	if (error) {
		return createPortal(
			<div
				className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center"
				style={{ zIndex: 999999 }}
				onClick={handleBackdropClick}
			>
				<button
					onClick={onClose}
					className="absolute top-8 right-8 text-white hover:text-red-500 transition-colors bg-black bg-opacity-70 rounded-full p-3 shadow-lg"
					style={{ zIndex: 1000000 }}
					aria-label="Close modal"
				>
					<IoClose size={32} />
				</button>
				<div className="text-red-500 text-2xl text-center">
					<div className="text-6xl mb-4">⚠️</div>
					Failed to load trailer
				</div>
			</div>,
			document.body
		);
	}

	// Show placeholder if no trailer available
	if (!trailerVideo || !trailerVideo.key) {
		return createPortal(
			<div
				className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center"
				style={{ zIndex: 999999 }}
				onClick={handleBackdropClick}
			>
				<button
					onClick={onClose}
					className="absolute top-8 right-8 text-white hover:text-red-500 transition-colors bg-black bg-opacity-70 rounded-full p-3 shadow-lg"
					style={{ zIndex: 1000000 }}
					aria-label="Close modal"
				>
					<IoClose size={32} />
				</button>
				<div className="text-white text-2xl text-center">
					<div className="text-6xl mb-4">🎬</div>
					No trailer available
				</div>
			</div>,
			document.body
		);
	}

	const embedUrl = `https://www.youtube.com/embed/${trailerVideo.key}?autoplay=1&mute=0&controls=1&showinfo=0&rel=0&modestbranding=1`;

	return createPortal(
		<div
			className="fixed inset-0 bg-black bg-opacity-95"
			style={{ zIndex: 999999 }} // Use inline style for maximum specificity
			onClick={handleBackdropClick}
		>
			{/* Close button */}
			<button
				onClick={onClose}
				className="absolute top-8 right-8 text-white hover:text-red-500 transition-colors bg-black bg-opacity-70 rounded-full p-3 shadow-lg"
				style={{ zIndex: 1000000 }}
				aria-label="Close modal"
			>
				<IoClose size={32} />
			</button>

			{/* Video container */}
			<div className="flex items-center justify-center h-full p-8">
				<div className="relative w-full h-full max-h-full">
					{iframeLoading && (
						<div className="absolute inset-0 flex items-center justify-center">
							<div className="text-white text-xl">
								<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
							</div>
						</div>
					)}
					<iframe
						src={embedUrl}
						title={`${trailerVideo.name || "Movie Trailer"}`}
						className="w-full h-full rounded-lg"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						allowFullScreen
						onLoad={handleIframeLoad}
						style={{ minHeight: "400px" }}
					/>
				</div>
			</div>
		</div>,
		document.body // Render directly to body to escape all stacking contexts
	);
};

export default Modal;
